import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOK_URL, IMAGES } from "@/lib/site";
import { MaskedLine } from "@/components/motion-ui";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const yBlob = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-5 md:px-8 overflow-hidden"
    >
      <motion.div
        style={{ y: yBlob }}
        className="absolute -top-24 -right-24 w-[520px] h-[520px] blob-pink pointer-events-none"
      />
      <motion.div
        style={{ y: yBlob }}
        className="absolute top-40 -left-32 w-[420px] h-[420px] blob-cyan pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-pink-200 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-pink-600 shadow-sm"
          >
            <Sparkles size={14} /> Institut bien-être • BBL Natural Sculpt
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-black uppercase leading-[0.92] tracking-tight text-neutral-900"
          >
            <MaskedLine delay={0.15}>BBL</MaskedLine>
            <MaskedLine delay={0.3} className="text-pink-600">
              NATURAL
            </MaskedLine>
            <MaskedLine delay={0.45} className="text-outline-pink">
              SCULPT
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.75 }}
            className="mt-7 max-w-xl text-lg md:text-xl text-neutral-700 leading-relaxed font-medium"
          >
            <span className="font-bold text-neutral-900">BBL Natural Sculpt</span>{" "}
            by <span className="font-bold text-neutral-900">Perle Lama</span> :
            sculpte ton corps, draine ton lymphatique et dis bye au burn-out.
            Élastiques LAGIGAFAM, énergie tropicale 🌴, résultats bien réels.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <Button
              asChild
              data-testid="hero-book-button"
              className="rounded-full bg-pink-600 hover:bg-pink-700 text-white h-14 px-8 text-base font-bold shadow-[0_12px_40px_rgba(255,0,127,0.35)] hover:scale-[1.03] transition-transform"
            >
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
                Prendre rendez-vous <ArrowRight className="ml-1" size={18} />
              </a>
            </Button>
            <Button
              asChild
              data-testid="hero-services-button"
              variant="outline"
              className="rounded-full border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white h-14 px-8 text-base font-bold bg-white"
            >
              <a href="#services">Découvrir les services</a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 flex items-center gap-6 flex-wrap"
          >
            <div className="flex -space-x-3">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-10 w-10 rounded-full border-2 border-white"
                  style={{
                    background: ["#FF007F", "#FF6B6B", "#FFD700", "#00CED1"][i],
                  }}
                />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-yellow-500">★★★★★</div>
              <p className="text-sm font-semibold text-neutral-700">
                +500 femmes transformées avec Perle Lama
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Image card with parallax + scale */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 0.8, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <motion.div
            style={{ y: yImg, scale: scaleImg }}
            className="relative rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(255,0,127,0.45)] border-4 border-white"
          >
            <img
              src={IMAGES.heroFitness}
              alt="Coach Perle Lama avec élastiques LAGIGAFAM"
              className="w-full h-[560px] object-cover"
              data-testid="hero-image"
            />
            {/* Spotlight gradient */}
            <div className="absolute inset-0 bg-gradient-to-tr from-pink-600/20 via-transparent to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
              <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-4 py-2 mb-2">
                <Heart size={14} className="text-pink-600" fill="#FF007F" />
                <span className="text-xs font-bold uppercase tracking-widest text-neutral-900">
                  Méthode signature
                </span>
              </div>
              <p className="text-white font-display text-2xl font-extrabold">
                Renforce. Drain. Glow.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 1.2, type: "spring" }}
            className="absolute -bottom-6 -left-6 md:-left-10 bg-yellow-400 text-neutral-900 rounded-3xl p-5 shadow-2xl animate-float w-44 hidden sm:block"
            data-testid="hero-sticker"
          >
            <p className="font-display text-3xl font-black leading-none">100%</p>
            <p className="text-xs font-bold uppercase tracking-widest mt-1">Naturel</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: 20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 1.3, type: "spring" }}
            className="absolute -top-6 -right-4 md:-right-8 bg-cyan-400 text-neutral-900 rounded-full h-24 w-24 flex items-center justify-center shadow-2xl animate-float"
          >
            <p className="font-display text-center text-xs font-black leading-tight">
              ANTI<br />BURN<br />OUT
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
